import { useState, useEffect } from 'react';
import { Container, Typography, Grid, Box, Avatar, Paper, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import InfoIcon from '@mui/icons-material/Info';
import Footer from '../components/Footer';

const TimelineItem = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
  border: `1px solid ${theme.palette.primary.main}`,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
  transition: 'transform 0.2s, box-shadow 0.2s',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)',
  },
}));

const LeadershipCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  height: '100%',
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
  border: `1px solid ${theme.palette.primary.main}`,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
  transition: 'all 0.3s ease',
  position: 'relative',
  overflow: 'hidden',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)',
  },
  // Add class for hover targeting
  '&.leadership-card': {
    // This class will be used for hover targeting
  },
}));

const BioOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: theme.palette.primary.dark,
  color: 'white',
  padding: theme.spacing(3),
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  opacity: 0,
  transform: 'translateY(100%)',
  transition: 'all 0.3s ease',
  zIndex: 10,
  overflowY: 'auto',
  '&.visible': {
    opacity: 1,
    transform: 'translateY(0)',
  },
  // Desktop hover effect
  '@media (hover: hover)': {
    '.leadership-card:hover &': {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
}));

const HeroSection = styled(Box)(({ theme }) => ({
  height: '80vh',
  minHeight: '600px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'white',
  position: 'relative',
  marginBottom: theme.spacing(8) ,
  overflow: 'hidden',
  '& > *': {
    position: 'relative',
    zIndex: 2,
  },
  [theme.breakpoints.down('md')]: {
    height: '70vh',
    minHeight: '500px',
  },
  [theme.breakpoints.down('sm')]: {
    height: '60vh',
    minHeight: '400px',
  },
}));

const BackgroundImage = styled(Box)(({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  zIndex: 1,
  transition: 'opacity 0.1s ease-in-out',
}));

const InfoIconButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  bottom: theme.spacing(1),
  right: theme.spacing(1),
  backgroundColor: 'rgba(46, 125, 50, 0.1)',
  color: theme.palette.primary.main,
  '&:hover': {
    backgroundColor: 'rgba(46, 125, 50, 0.2)',
  },
  '&.expanded': {
    backgroundColor: 'rgba(46, 125, 50, 0.3)',
    color: theme.palette.primary.dark,
  },
}));

const leadership = [
  {
    name: 'Vince Scott',
    title: 'President and CEO',
    bio: `Mr. Scott has been the Developer and Broker representing over 1.9 million square feet and 2,500 units of commercial and multi-family developments while managing investor funds and budgets of over $1.3 Billion dollars over a 30-year development career of both equity and debt.

As a Broker/Owner in both California and Nevada, Mr. Scott has personally closed over $3.8 Billion Dollars in Gross Closed Transactions while setting the records for closing the highest priced land and hotel closings in Lake Tahoe of $47 Million for the Chateau at the Village, South Lake Tahoe and the Tahoe Biltmore Hotel Casino at $29mm in Crystal Bay, NV.

Mr. Scott is an avid outdoor enthusiast and committed to the preservation of Lake Tahoe Air and Water Quality as a Sustainable Resort Developer.`,
    image: '/images/vince-headshot.jpg'
  },
  {
    name: 'Scott Brackett',
    title: 'Chief Operating Officer',
    bio: `A fifth-generation native of California and second-generation real estate broker, Scott graduated from Arizona State University with degrees in Economics and Finance.

Licensed in 1993, he worked for his uncle, owner and president of Frank Howard Allen Realtors, the largest independent brokerage firm in Marin, Sonoma & Napa counties. In 2013, Frank Howard Allen sold the firm.

In 2014 Scott became co-founding managing member of Engel & Völkers-Gestalt Group and served as the Chief Operating Officer. Under his leadership, the firm grew to 35+ offices and 1200+ real estate advisors. In 2020 Scott sold his ownership and remained with the firm through 2024.

Mr. Brackett partnered with Vince Scott in 2018 with the vision of developing the Grand Tahoe Blue Resort & Residence Club. He currently serves as the COO of Scott Development Companies & Clearwater Resort Holdings.

Brackett is a strategic thinker with tactical execution in delivering optimum results. Strong analytical skills, adept in metric management and extensive background in P&L accountability. With over 30 years of experience, having held key senior level positions and specializing in developing high performance business plans for complex resort development projects and commercial real estate, positions our company for significant growth opportunity.`,
    image: '/images/scott-headshot.jpg'
  },
  {
    name: 'Brent Morgheim',
    title: 'Project Manager',
    bio: `Brent Morgheim is an accomplished construction management and engineering professional with a strong background in commercial real estate development, private equity, and complex project oversight.

As the Director of Operations at Elevations Engineering Group, a private equity and engineering firm he co-founded, Brent manages all operational coordination and contract management, including engineering, construction, and cryptocurrency forensic investigations. His recent work includes zoning calculations for commercial real estate in Los Angeles, developing business plans, and coordinating design/build projects for major entertainment companies like Netflix.

He holds a Bachelor of Science in Construction Management/Engineering from the University of Nebraska-Lincoln and is an AWS Certified Welding Inspector and a Cryptocurrency Forensic Investigator.

Previously, Brent held key project management roles at Butier Engineering, where he served as Lead Construction Engineering Project Manager for significant projects at John Wayne Airport, overseeing a $40 million Air Handling Unit installation and a $125 million terminal renovation. He also provided engineering/construction management consulting as an owner's representative for the $1.2 billion Carlsbad Desalination Project, where he managed welding operations and ensured project quality assurance.

Brent's expertise spans project administration, document control, budget tracking, and leading project meetings, with proficiency in various industry software like Bluebeam Revu Extreme, Navisworks, and Primavera P6.`,
    image: '/images/brent-headshot.jpeg'
  },
  {
    name: 'Kyle Morgheim',
    title: 'Project Manager',
    bio: `Mr Morgheim has a comprehensive set of skills for land development and construction management.
Kyle is a professionally licensed Civil Engineer in the state of California with a Masters Degree in
Finance. Kyle has over 20 years experience in civil engineering design, project and construction
management. He has worked on both the private and public sides of the industry.
On the private side, Kyle is the Principal at Elevations Engineering, a Consulting Engineering design
company that he founded. The company primarily performs engineering design and analysis for roadway
and utility design, hydrologic/hydraulic modelling and analysis, storm drain design, water quality
analysis, earthwork analysis, site layouts/design, grading and erosion control, land development, and cost
estimating. Kyle was the engineer of record for a 228 lot subdivision in Riverside County, CA. He also
has extensive design experience in Orange County, CA and San Diego County, CA and Colorado.
On the public side, Kyle has experience as a construction manager and Program Manager. At Butier
Engineering, he worked as the Program Manager for the City of Tustin water projects where he managed
the City water projects from Design, Bid, and through construction. Kyle performed construction
management and program management services for large public projects specialized in water /
wastewater treatment facilities, pump stations, reservoirs, pipelines, storm facilities, transportation, and
grading.

Mr. Morgheim is also skilled in performing due diligence analysis, cost analysis/estimating, Pro-
forma/other financial modelling, and business valuation methods.`,
    image: '/images/kyle-headshot.jpeg'
  },
  {
    name: 'Jen Mahler',
    title: 'Chief Sustainability Officer - Government Relations',
    bio: `Jennifer Mahler has over 30 years in real estate construction and development services, business consulting, and financial planning experience. She has spent extensive time both domestically and globally helping businesses launch and grow while focusing on sustainability, energy, fiscal responsibility, and positive community-driven outcomes. Jennifer brings a common-sense, no-nonsense approach to even the most sophisticated of projects.

Having been an owner / CEO and employer, Jen comes with an understanding of the requirements needed to build and sustain an enterprise while considering a changing global dynamics.

She with partners, launched Rivers Edge Group Inc in 2025 to focus on public - private partnerships for energy development to support industrial zones and community energy needs. Working closely with the data center industry to help enable large infrastructure projects to support a massively expanding need.`,
    image: '/images/DSC00669.jpg'
  },
  {
    name: 'Nicholas Patterson',
    title: 'Investor Relations - Family Offices',
    bio: `Nicholas Patterson is a growth strategist and capital markets advisor specializing in investor positioning, capital formation, and high-value deal flow. As the founder of Jupiter Strategies, he has generated over $50 million in client revenue through strategic growth systems, capital narratives, and investor acquisition frameworks. Patterson works with developers, funds, and financial platforms to position large-scale opportunities for family offices and institutional partners while accelerating funding and deal flow.`,
    image: '/images/IMG_0417.jpeg'
  },
  {
    name: 'Jeff Davis',
    title: 'CTO - Caddis Cloud Solutions',
    bio: `Jeff Davis is a seasoned technology and infrastructure executive with more than 35 years of experience spanning data center management, hardware platforms, network infrastructure, mission-critical facility design, construction strategy, and operational leadership. His career reflects deep expertise in developing and operating complex, high-availability environments that support large-scale enterprise and government workloads.

Jeff has led the development of multiple large energy and infrastructure initiatives, including energy strategy formulation, feasibility and utilization studies, power profile modeling, and navigation of state and federal regulatory frameworks. He has worked closely with utilities, engineering firms, and operations partners to design scalable solutions, while also developing associated business and financial models to support investment decisions. In parallel, he has structured and executed joint partnerships to enable long-term operational success.

Throughout his career, Jeff has collaborated with major global organizations including Dell, Intel, Microsoft, and Oracle Cloud. Notably, he led Oracle's data center engineering organization and later established and led the Oracle Cloud Government group. In that role, he was responsible for global federal strategy, as well as the design and operation of top-secret data center and IT environments supporting highly sensitive government missions.`,
    image: '/images/jeff-headshot.png'
  },
  {
    name: 'Sarah Adams',
    title: 'Director of Spa and Wellness Services',
    // bio: `Bio coming soon.`,
    image: '/images/02DE2932-ADD9-439D-9FD0-3D787F0849A9.jpeg'
  }
];

const milestones = [
  {
    year: '1996',
    title: 'Company Founded',
    description: 'Scott Companies established with a vision to build, support and sell the highest quality real estate in the Tahoe Basin.'
  },
  {
    year: '2000',
    title: 'Expansion of Services',
    description: 'Launched comprehensive development services including consulting, construction management, and commercial and residential sales.'
  },
  {
    year: '2010',
    title: 'Major Projects Completion',
    description: 'Successfully completed several landmark projects including Village Plaza, Indian Hills Retail Plaza, and Incline Athletic Club.'
  },
  {
    year: '2020',
    title: 'Sustainable Development Focus',
    description: 'Intensified focus on sustainable development practices and LEED-certified projects.'
  }
];

export default function About() {
  const [expandedCard, setExpandedCard] = useState<number | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const images = [
    '/images/nice1_better.png',
    '/images/nice2_better.png',
    '/images/4677_better.png'
  ];
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % images.length);
    }, 6000); // Switch every 6 seconds

    return () => clearInterval(interval);
  }, [images.length]);

  const handleCardClick = (index: number) => {
    setExpandedCard(expandedCard === index ? null : index);
  };

  return (
    <Box sx={{ width: '100%', height: '100%', minWidth: '100vw', minHeight: '100vh', bgcolor: 'background.default' }}>
        <HeroSection>
          <BackgroundImage
            sx={{
              backgroundImage: `linear-gradient(120deg, rgba(15, 23, 42, 0.55), rgba(15, 23, 42, 0.15)), url("${images[currentImageIndex]}")`,
            }}
          />
          <Container maxWidth="lg" sx={{ zIndex: 2 }}>
            <Grid container spacing={6} alignItems="center">
              <Grid item xs={12} md={7}>
                <Typography 
                  variant="overline"
                  sx={{
                    letterSpacing: '0.22em',
                    textTransform: 'uppercase',
                    color: 'rgba(241,245,249,0.9)',
                  }}
                >
                  Our Story
                </Typography>
                <Typography 
                  variant="h1" 
                  component="h1" 
                  gutterBottom 
                  sx={{ 
                    fontWeight: 700, 
                    textShadow: '0 4px 24px rgba(0,0,0,0.7)',
                    fontSize: { xs: '2.5rem', sm: '3.3rem', md: '3.8rem', lg: '4.1rem' },
                    mb: 2,
                    textAlign: { xs: 'center', md: 'left' },
                  }}
                >
                  Crafted with intention.
                  <br />
                  Designed to belong.
                </Typography>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    textShadow: '0 2px 12px rgba(0,0,0,0.6)',
                    fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.4rem' },
                    maxWidth: '640px',
                    mx: { xs: 'auto', md: 0 },
                    textAlign: { xs: 'center', md: 'left' },
                  }}
                >
                  For over three decades we’ve helped shape the built environment around Lake Tahoe—pairing disciplined development with a deep respect for the landscape.
                </Typography>
              </Grid>
            </Grid>
          </Container>
        </HeroSection>

        <Container sx={{ py: { xs: 4, md: 8 }, pb: 10 }}>
          <Grid container spacing={6} sx={{ mb: 8 }} alignItems="flex-start">
            <Grid item xs={12} md={7}>
              <Typography variant="h4" gutterBottom color="primary.main">
                About Scott Development
              </Typography>
              <Typography variant="body1" paragraph color="text.secondary">
                Established in 1996, Scott Companies is the vision of real estate developer and broker Vince Scott, 
                who is widely known as one of Tahoe&apos;s foremost business leaders. Our family of companies provides 
                a comprehensive array of real estate services, from consulting and development to construction 
                management and commercial and residential sales.
              </Typography>
              <Typography variant="body1" paragraph color="text.secondary">
                We pride ourselves on customer service excellence, offering the best experience for our clients, 
                investors and partners. We believe in conducting business openly and with integrity. Our people 
                are committed to innovative, environmentally responsible building, and we are dedicated to giving 
                back and helping the communities where we do business.
              </Typography>
            </Grid>
            <Grid item xs={12} md={5}>
              <Box
                sx={{
                  borderRadius: 3,
                  border: theme => `1px solid ${theme.palette.divider}`,
                  p: 3,
                  bgcolor: 'background.paper',
                  boxShadow: '0 20px 45px rgba(15, 23, 42, 0.06)',
                }}
              >
                <Typography variant="overline" sx={{ letterSpacing: '0.18em', textTransform: 'uppercase', mb: 1, display: 'block' }}>
                  At a glance
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="h4" color="primary.main">
                      30+
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      years of development & brokerage experience
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="h4" color="primary.main">
                      $3.8B
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      in closed transactions across resort and residential
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="h4" color="primary.main">
                      1.9M
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      square feet of commercial and mixed-use delivered
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="h4" color="primary.main">
                      Tahoe
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      basin-focused, sustainability-led development strategy
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ mb: 8 }}>
            <Typography variant="h4" gutterBottom color="primary.main">
              Our Team
            </Typography>
            <Grid container spacing={3}>
              {leadership.map((member, index) => (
                <Grid item xs={12} sm={6} md={6} key={index}>
                  <LeadershipCard 
                    onClick={member.bio ? () => handleCardClick(index) : undefined}
                    className={member.bio ? 'leadership-card' : ''}
                    sx={{ cursor: member.bio ? 'pointer' : 'default' }}
                  >
                    <Box sx={{ textAlign: 'center' }}>
                      <Avatar
                        src={member.image}
                        alt={member.name}
                        sx={{ 
                          width: 130, 
                          height: 130, 
                          border: '3px solid', 
                          borderColor: 'primary.light',
                          mx: 'auto',
                          mb: 2
                        }}
                      />
                      <Typography variant="h6" gutterBottom>
                        {member.name}
                      </Typography>
                      <Typography variant="subtitle2" color="primary" gutterBottom>
                        {member.title}
                      </Typography>
                    </Box>
                    {member.bio && (
                      <>
                        <InfoIconButton 
                          className={expandedCard === index ? 'expanded' : ''}
                          size="small"
                        >
                          <InfoIcon fontSize="small" />
                        </InfoIconButton>
                        <BioOverlay 
                          className={`bio-overlay ${expandedCard === index ? 'visible' : ''}`}
                          onClick={() => handleCardClick(index)}
                        >
                          <Typography variant="body2" sx={{ textAlign: 'center', lineHeight: 1.6 }}>
                            {member.bio}
                          </Typography>
                        </BioOverlay>
                      </>
                    )}
                  </LeadershipCard>
                </Grid>
              ))}
            </Grid>
          </Box>

          <Box>
            <Typography variant="h4" gutterBottom color="primary.main">
              Our Journey
            </Typography>
            <Box sx={{ position: 'relative', mt: 3 }}>
              <Box
                sx={{
                  position: 'absolute',
                  left: 16,
                  top: 0,
                  bottom: 0,
                  width: '2px',
                  bgcolor: theme => theme.palette.divider,
                  display: { xs: 'none', sm: 'block' },
                }}
              />
              {milestones.map((milestone, index) => (
                <TimelineItem
                  key={index}
                  sx={{
                    position: 'relative',
                    pl: { xs: 4, sm: 6 },
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      left: { xs: 0, sm: 8 },
                      top: 32,
                      width: 12,
                      height: 12,
                      marginLeft: 1,
                      borderRadius: '999px',
                      bgcolor: 'background.paper',
                      border: theme => `2px solid ${theme.palette.primary.main}`,
                      transform: { xs: 'translateX(-9999px)', sm: 'translateX(-50%)' },
                      display: { xs: 'none', sm: 'block' },
                    }}
                  />
                  <Typography variant="overline" color="primary" sx={{ letterSpacing: '0.18em' }}>
                    {milestone.year}
                  </Typography>
                  <Typography variant="h5" gutterBottom>
                    {milestone.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {milestone.description}
                  </Typography>
                </TimelineItem>
              ))}
            </Box>
          </Box>
        </Container>
        <Footer />
      </Box>
  );
} 